import React, { useState, useEffect } from "react";
import { Modal, Radio, Tag, Alert } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { $t } from "~/i18n.js";
import { message } from "antd";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { useDispatch, useSelector } from "react-redux";
import { daysActions } from "~/redux/actions.js";
import { updateIfNeeded } from "~/lib/helpers.js";
import { TimeFormat } from "~/components/date/TimeFormat.jsx";
import { _parseDate } from "~/lib/localize.js";
import { colors } from "~/theme/colors.js";
import { api } from "~/lib/api.js";

export const CheckinModal = ({ visible, onClose, currentMaterial }) => {
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Early exit if modal is not visible
  if (!visible) {
    return null;
  }

  const dispatch = useDispatch();
  const days = useSelector((state) => state.days);
  const allDays = days?.allDays || [];

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toJSON().slice(0, 10);

  // Filter days for today
  const todaysDays = allDays.filter(d => d.datum === today && d.is_vliegend && d.gaat_door)

  useEffect(() => {
    visible &&
      updateIfNeeded(days.lastUpdated, () =>
        dispatch(daysActions.getAllDays())
      );
  }, [visible, days.lastUpdated, dispatch]);

  // Auto-select if only one day available
  useEffect(() => {
    if (todaysDays.length === 1 && !selectedDay) {
      setSelectedDay(todaysDays[0]);
    } else if (todaysDays.length === 0) {
      setSelectedDay(null);
    }
  }, [todaysDays, selectedDay]);

  const checkin = async () => {
    if (!selectedDay) {
      message.error($t("Selecteer eerst een dag"));
      return;
    }

    setLoading(true);
    try {
      // Dummy function - will be implemented later
      const data = api.post("flights/checkin.json", {
        dag_id: selectedDay.dag_id,
        kist_id: currentMaterial.kist_id,
      });
      console.log("Checkin data:", data);
      if (data) {
        message.success({
          content: $t(
            "Je bent ingecheckt! Er is een nieuwe vlucht aangemaakt."
          ),
          duration: 3,
        });
        onClose(true);
      }
    } catch (error) {
      console.error("Checkin failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDay = (day) => {
    setSelectedDay(day);
  };

  return (
    <Modal
      title={$t("Check-in vlucht")}
      open={visible}
      confirmLoading={loading}
      onCancel={() => onClose(false)}
      okText={$t("Check in")}
      cancelText={$t("Annuleren")}
      onOk={checkin}
      okType="primary"
      okButtonProps={{
        icon: <PlusOutlined />,
        disabled: !selectedDay,
      }}
    >
      <div className="column" style={{ gap: 12, alignItems: "flex-start" }}>
        <HelpLine>
          {$t(
            "Je kan alvast inchecken, een nieuwe vlucht wordt dan aangemaakt en klaargezet in de startadministratie."
          )}
        </HelpLine>

        {currentMaterial && (
          <div>
            <strong>{$t("Vliegtuig")}: </strong>
            {currentMaterial.name}
          </div>
        )}

        {todaysDays.length === 1 && selectedDay && (
          <p>
            <strong>{$t("Dag")}:</strong> {_parseDate(selectedDay.datum)} -{" "}
            {selectedDay.type_dag}
          </p>
        )}

        {todaysDays.length !== 1 && <div style={{ width: "100%" }}>
          <p>
            <strong>{$t("Selecteer een dag")}:</strong>
          </p>

          {todaysDays.length === 0 && (
            <Alert
              message={$t("Er is geen dag aangemaakt, maak eerst een dag aan")}
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}

          {todaysDays.length > 1 && (
            <Radio.Group
              size="large"
              value={selectedDay?.dag_id}
              onChange={(e) => {
                const day = todaysDays.find((d) => d.dag_id === e.target.value);
                handleSelectDay(day);
              }}
              style={{ width: "100%", marginTop: 8 }}
            >
              {todaysDays.map((day) => (
                <Radio
                  key={day.dag_id}
                  value={day.dag_id}
                  style={{
                    display: "flex",
                    padding: "4px 0",
                    width: "100%",
                  }}
                >
                  <div className="row" style={{ gap: 8 }}>
                    <TimeFormat date={day.briefing_tijd} />
                    <span style={{ fontWeight: 500 }}>{day.type_dag}</span>
                    {day.titel && (
                      <span style={{ color: colors.gray }}>{day.titel}</span>
                    )}
                  </div>
                </Radio>
              ))}
            </Radio.Group>
          )}
        </div>}

        {todaysDays.length > 0 && (
            <Alert
              message={$t("Let op: Indien er al een vlucht klaarstaat (dus niet gestart) dan zal hij de gezagvoerder van die vlucht overschrijven.")}
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
        )}

      </div>
    </Modal>
  );
};
